/**
 * composition/resolve.ts
 *
 * Resolves an agent's `composes:` items against the Library into a
 * ResolvedComposition — prompt blocks for inline items and discoverable
 * entries for tool-list items.
 *
 * Three resolution paths per binding:
 *   - `inline-snapshot`: reads frozen content from compile_manifest.json
 *   - `inline-live`: reads current content from Library (per invocation)
 *   - `discoverable`: registers as a capability (name + description + content)
 *
 * This module is pure logic — no IO beyond the IAssetIndex calls.
 */

import { createHash } from "node:crypto";
import type { IAssetIndex } from "@skaile/workspaces/library";
import type { AgentComposeItem } from "@skaile/workspaces/types/manifests";
import { type CompileManifest, type ResolvedComposition, resolveBinding } from "./types.js";

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

/**
 * Options for {@link resolveComposition}.
 *
 * @docLink packages/runner/capabilities#resolve-composition
 */
export interface ResolveCompositionOpts {
  /** The agent's composes: items */
  items: AgentComposeItem[];
  /** Library for asset resolution */
  library: IAssetIndex;
  /**
   * Pre-loaded compile manifest for inline-snapshot items.
   * If absent, inline-snapshot items fall back to live resolution with a warning.
   */
  compileManifest?: CompileManifest;
  /**
   * Content loader — given an asset ref, returns the asset's prompt-injectable content.
   * For skill: the SKILL.md body. For persona/ruleset: the manifest body text.
   * Returns null if the content cannot be loaded.
   */
  loadContent: (ref: string, kind: string) => Promise<string | null>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve all composition items for an agent.
 *
 * Returns:
 *   - inlineBlocks: content to inject into the system prompt
 *   - discoverableEntries: items to register as LLM-visible capabilities
 *   - warnings: non-fatal issues encountered during resolution
 *
 * @param opts - Resolution options with items, library, optional compile manifest and content loader.
 * @returns A {@link ResolvedComposition} with inline blocks, discoverable entries, and warnings.
 * @docLink packages/runner/capabilities#resolve-composition
 */
export async function resolveComposition(
  opts: ResolveCompositionOpts,
): Promise<ResolvedComposition> {
  const { items, library, compileManifest, loadContent } = opts;
  const result: ResolvedComposition = {
    inlineBlocks: [],
    discoverableEntries: [],
    warnings: [],
  };

  for (const item of items) {
    const binding = resolveBinding(item);

    try {
      switch (binding) {
        case "inline-snapshot":
          await resolveInlineSnapshot(item, binding, compileManifest, loadContent, result);
          break;
        case "inline-live":
          await resolveInlineLive(item, binding, loadContent, result);
          break;
        case "discoverable":
          await resolveDiscoverable(item, library, loadContent, result);
          break;
      }
    } catch (err) {
      result.warnings.push(
        `Failed to resolve "${item.ref}" (${item.kind}): ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Internal: per-binding resolution
// ---------------------------------------------------------------------------

async function resolveInlineSnapshot(
  item: AgentComposeItem,
  binding: "inline-snapshot",
  compileManifest: CompileManifest | undefined,
  loadContent: (ref: string, kind: string) => Promise<string | null>,
  result: ResolvedComposition,
): Promise<void> {
  // Try to find frozen content in the compile manifest
  if (compileManifest) {
    const entry = compileManifest.entries.find(
      (e) => e.asset_ref === item.ref && e.binding === "inline-snapshot",
    );
    if (entry) {
      const content = await loadContent(item.ref, item.kind);
      if (content) {
        // Verify content hasn't drifted since last rebuild
        const hash = createHash("sha256").update(content).digest("hex");
        if (hash !== entry.hash) {
          result.warnings.push(
            `"${item.ref}" (${item.kind}) content has changed since last rebuild (expected ${entry.hash.slice(0, 8)}…, got ${hash.slice(0, 8)}…). Run "skaile rebuild" to update the snapshot.`,
          );
        }
        result.inlineBlocks.push({ ref: item.ref, kind: item.kind, content, binding });
        return;
      }
    }
  }

  // Fallback: resolve live (with warning that rebuild is recommended)
  const content = await loadContent(item.ref, item.kind);
  if (content) {
    result.warnings.push(
      `"${item.ref}" (${item.kind}) is binding=inline-snapshot but no compile_manifest.json found — using live content. Run "skaile rebuild" to freeze.`,
    );
    result.inlineBlocks.push({ ref: item.ref, kind: item.kind, content, binding });
  } else {
    result.warnings.push(`"${item.ref}" (${item.kind}): content not found`);
  }
}

async function resolveInlineLive(
  item: AgentComposeItem,
  binding: "inline-live",
  loadContent: (ref: string, kind: string) => Promise<string | null>,
  result: ResolvedComposition,
): Promise<void> {
  const content = await loadContent(item.ref, item.kind);
  if (content) {
    result.inlineBlocks.push({ ref: item.ref, kind: item.kind, content, binding });
  } else {
    result.warnings.push(`"${item.ref}" (${item.kind}): content not found`);
  }
}

async function resolveDiscoverable(
  item: AgentComposeItem,
  library: IAssetIndex,
  loadContent: (ref: string, kind: string) => Promise<string | null>,
  result: ResolvedComposition,
): Promise<void> {
  // Look up the asset definition for metadata. This is optional enrichment —
  // a library failure (missing/corrupt/migrating index DB) must never drop the
  // entry, so swallow the error and fall back to the default description.
  let assetDef: Awaited<ReturnType<IAssetIndex["getAssetDef"]>> = null;
  try {
    assetDef = await library.getAssetDef(item.ref);
  } catch (err) {
    assetDef = null;
    result.warnings.push(
      `"${item.ref}" (${item.kind}): library lookup failed (${err instanceof Error ? err.message : String(err)}) — using fallback description`,
    );
  }

  // Build the tool name from the ref
  const name = buildToolName(item);
  const description = assetDef?.description ?? `${item.kind}: ${item.ref}`;

  // Load full content for on-demand usage
  const content = await loadContent(item.ref, item.kind);
  if (content) {
    result.discoverableEntries.push({
      ref: item.ref,
      kind: item.kind,
      name,
      description,
      content,
    });
  } else {
    result.warnings.push(`"${item.ref}" (${item.kind}): content not found for discoverable entry`);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a capability tool name from a compose item ref.
 * e.g. '@skaile/gmail@^1' -> 'skaile__gmail'
 * e.g. '@acme/sales-research@^3' -> 'acme__sales-research'
 * e.g. '@skaile/gmail' (no version) -> 'skaile__gmail'
 */
function buildToolName(item: AgentComposeItem): string {
  // Strip version pin: only strip @<version> if it appears after the name
  // (i.e. after the last /). Scoped refs like @skaile/gmail have the @
  // as the publisher prefix — we must not strip that.
  const lastSlash = item.ref.lastIndexOf("/");
  let refNoVersion: string;
  if (lastSlash !== -1) {
    const afterSlash = item.ref.slice(lastSlash + 1);
    const versionAt = afterSlash.indexOf("@");
    refNoVersion = versionAt !== -1 ? item.ref.slice(0, lastSlash + 1 + versionAt) : item.ref;
  } else {
    // No slash — bare ref like 'gmail@^1'
    const versionAt = item.ref.indexOf("@");
    refNoVersion = versionAt !== -1 ? item.ref.slice(0, versionAt) : item.ref;
  }
  // Strip leading @
  const cleaned = refNoVersion.replace(/^@/, "");
  // Replace / with __ for flat tool namespace
  return cleaned.replace(/\//g, "__");
}
